import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useOfferStore, useQuoteTotals, type QuotePersistenceIdentity } from '@/lib/store';
import { formatCurrency } from '@/lib/utils';
import { normalizePersistedQuoteEditorState } from '@shared/quote-editor-state';
import { getQuoteFileStem } from '@shared/quote-naming';
import { normalizeVatRate, isExcludedFromMainSum } from '@/lib/quote-calculations';
import { countMissingPrices } from '@shared/quote-totals';
import DocxExport from './docx-export';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle, 
  DialogTrigger
} from '@/components/ui/dialog';
import { 
  DndContext, 
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Database, ChevronDown, ChevronRight, Sparkles } from 'lucide-react';
import PositionAiTextDialog, { type PositionAiTextTarget } from './ui/position-ai-text-dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { exportQuoteDocx, DocxGenerationMethod } from '@/lib/document-export';
import { Download, FileText, FileDown, Calculator, Building2, Euro, Package, Users, Calendar, Save, Undo2, AlertCircle } from 'lucide-react';
import { saveQuoteData, type QuoteDataPackage } from '@/lib/quote-data-service';
import {
  buildQuoteSaveRequest,
  getSavedQuotePersistenceIdentity,
  type QuoteSaveSnapshot,
} from '@/lib/quote-save-coordinator';
import {
  createQuoteAutosaveQueue,
  type QuoteAutosaveQueue,
} from '@/lib/quote-autosave-queue';
import { buildQuoteEditorUrl } from '@/lib/dashboard-navigation';
import { useToast } from '@/hooks/use-toast';
import { useLocation } from 'wouter';
import { useAuth } from '@/lib/auth';
import GAEBExport from './gaeb/GAEBExport';
import X84PriceImport from './gaeb/X84PriceImport';
import GaebTeilExport from './gaeb/GaebTeilExport';
import UrkalkulationExport from './urkalkulation-export';
import BlankoLvExport from './blanko-lv-export';
import GrosshandelAnfrageExport from './grosshandel-anfrage-export';
import { SoftecExportButton } from './softec-export-button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FileUp, Scissors } from 'lucide-react';

function isQuoteRevisionConflict(error: unknown): boolean {
  return (
    (error instanceof Error && error.message === 'QUOTE_REVISION_CONFLICT')
    || (
      typeof error === 'object'
      && error !== null
      && 'status' in error
      && error.status === 409
    )
  );
}

// Sortable Gewerk Item Component
function SortableGewerkItem({ id, name, items, netTotal, onAiClick }: { id: string, name: string, items: any[], netTotal: number, onAiClick?: () => void }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="border-l-2 border-primary pl-2 py-2 bg-white rounded-sm shadow-sm mb-2"
    >
      <div className="flex justify-between items-center mb-1">
        <div className="flex-1 flex items-center min-w-0">
          <button
            className="mr-2 cursor-grab touch-none p-1 text-gray-400 hover:text-gray-600"
            {...attributes}
            {...listeners}
          >
            <GripVertical size={16} />
          </button>
          <span className="text-sm font-medium text-gray-800 truncate">{name}</span>
          {/* KI-Formulierungshilfe je Titel (Wunsch Malermeister): benennt den
              Titel über ALLE Zeilen der Gruppe um — erst nach Vorher/Nachher. */}
          {onAiClick && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onAiClick}
              className="ml-1 h-5 w-5 flex-shrink-0 p-0 text-purple-400 hover:text-purple-700 hover:bg-purple-50"
              title="KI-Formulierungshilfe für diesen Titel (Vorher/Nachher, Sie entscheiden)"
              aria-label={`KI-Formulierungshilfe für Titel ${name}`}
              data-testid={`titel-ai-button-${name}`}
            >
              <Sparkles className="h-3 w-3" />
            </Button>
          )}
        </div>
        <span className="text-sm font-medium">{formatCurrency(netTotal)}</span>
      </div>
      <div className="text-xs text-gray-600 ml-7">
        {items.length} Position{items.length !== 1 ? 'en' : ''}
      </div>
    </div>
  );
}

export default function QuoteSidebar() {
  // Individual selectors — whole-store useOfferStore() causes React #185
  const positions = useOfferStore(s => s.positions);
  const gewerkOrder = useOfferStore(s => s.gewerkOrder);
  const reorderGewerk = useOfferStore(s => s.reorderGewerk);
  const quoteItems = useOfferStore(s => s.quoteItems);
  const quoteHeader = useOfferStore(s => s.quoteHeader);
  const discountAmount = useOfferStore(s => s.discountAmount);
  const discountType = useOfferStore(s => s.discountType);
  const discountApplicationMode = useOfferStore(s => s.discountApplicationMode);
  const setDiscount = useOfferStore(s => s.setDiscount);
  const setDiscountApplicationMode = useOfferStore(s => s.setDiscountApplicationMode);
  const applyDistributedDiscount = useOfferStore(s => s.applyDistributedDiscount);
  const clearDiscount = useOfferStore(s => s.clearDiscount);
  const isDiscountAppliedToPrices = useOfferStore(s => s.isDiscountAppliedToPrices);
  const originalPrices = useOfferStore(s => s.originalPrices);
  const numberingMode = useOfferStore(s => s.numberingMode);
  const currentQuoteSlot = useOfferStore(s => s.currentQuoteSlot);
  const currentQuoteStorageRevision = useOfferStore(s => s.currentQuoteStorageRevision);
  const setCurrentQuotePersistence = useOfferStore(s => s.setCurrentQuotePersistence);
  const loadQuoteData = useOfferStore(s => s.loadQuoteData);
  const setGlobalVatRate = useOfferStore(s => s.setGlobalVatRate);
  const isGAEBDatabase = useOfferStore(s => s.isGAEBDatabase);
  const currentDatabaseId = useOfferStore(s => s.currentDatabaseId);
  const gaebMetadata = useOfferStore(s => s.gaebMetadata);
  const offerDocumentEpoch = useOfferStore(s => s.offerDocumentEpoch);
  const ensureQuoteNumberAllocated = useOfferStore(s => s.ensureQuoteNumberAllocated);

  // Auffangnetz: Editor offen, aber noch keine Angebotsnummer (z.B. Angebot
  // direkt aus einer Datenbank aufgebaut) → Nummer aus dem Nummernkreis holen.
  // Läuft nur EINMAL pro Dokument (Epoch): wer die Nummer später bewusst
  // leert, bekommt sie nicht ungefragt überschrieben. Der Store dedupliziert
  // zusätzlich pro Epoch — keine Doppelvergaben.
  useEffect(() => {
    if (!(useOfferStore.getState().quoteHeader.angebot_nr || '').trim()) {
      ensureQuoteNumberAllocated();
    }
  }, [offerDocumentEpoch, ensureQuoteNumberAllocated]);

  const { gewerkGroups, totalNet, bedarfNet, alternativNet } = useQuoteTotals();
  const { hasInvoiceAccess } = useAuth();
  const [, navigate] = useLocation();
  const [isGewerkOpen, setIsGewerkOpen] = useState(true);
  // KI-Formulierungshilfe je Titel: hält den Gruppennamen des angeklickten Titels.
  const [titelAiName, setTitelAiName] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  // Export-Menü (Welle 1.3): persistenter In-Flight-Guard für die "einfachen"
  // Exporte (überlebt das Unmount des Menü-Contents beim Schließen) und
  // controlled-Open-States für die Dialog-Exporte, die als Geschwister des
  // DropdownMenu gemountet werden (kein Überlappen nach Dialog-Schließen).
  const [exportBusy, setExportBusy] = useState(false);
  const [x84ImportOpen, setX84ImportOpen] = useState(false);
  const [gaebExportOpen, setGaebExportOpen] = useState(false);
  const [teilExportOpen, setTeilExportOpen] = useState(false);
  // Gate für die GAEB-Dialog-Exporte (identisch zur internen Bedingung in
  // GAEBExport/GaebTeilExport): nur im geladenen GAEB-Modus sinnvoll.
  const isGaebMode = isGAEBDatabase && !!gaebMetadata && currentDatabaseId != null;
  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);
  const [lastAutoSave, setLastAutoSave] = useState<Date | null>(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [hasSaveConflict, setHasSaveConflict] = useState(false);
  const { toast } = useToast();
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const autoSaveStatusResetRef = useRef<NodeJS.Timeout | null>(null);
  const quoteAutosaveQueueRef = useRef<QuoteAutosaveQueue<QuoteSaveSnapshot> | null>(null);
  const isDocumentTransitionRef = useRef(false);
  const fallbackDateRef = useRef(new Date().toISOString().split('T')[0]);
  // Deterministischer Autosave-Platzhalter statt Zufallsnummer: die echte Nummer
  // kommt aus dem Nummernkreis (ensureQuoteNumberAllocated unten). Der Platzhalter
  // greift nur, solange die Server-Vergabe noch aussteht oder fehlschlägt.
  const autoSaveOfferNumberRef = useRef(`Entwurf-${fallbackDateRef.current}`);

  const vatRate = normalizeVatRate(quoteHeader.mwstSatz);
  const hasTotalDiscount = !isDiscountAppliedToPrices && discountAmount > 0;
  const totalDiscountValueRaw = hasTotalDiscount
    ? (discountType === 'percent'
      ? (totalNet * discountAmount) / 100
      : discountAmount)
    : 0;
  const totalDiscountValue = Math.max(0, Math.min(totalNet, Math.round(totalDiscountValueRaw * 100) / 100));
  const netAfterTotalDiscount = Math.max(0, Math.round((totalNet - totalDiscountValue) * 100) / 100);
  const totalTaxAfterDiscount = Math.round(netAfterTotalDiscount * (vatRate / 100) * 100) / 100;
  const totalGrossAfterDiscount = Math.round((netAfterTotalDiscount + totalTaxAfterDiscount) * 100) / 100;
  const normalizedEditorState = normalizePersistedQuoteEditorState({
    discountAmount,
    discountType,
    discountApplicationMode,
    originalPrices,
    isDiscountAppliedToPrices,
    numberingMode,
  });
  const quoteSaveSnapshot: QuoteSaveSnapshot = {
    header: {
      ...quoteHeader,
      angebot_nr: quoteHeader.angebot_nr?.trim() || autoSaveOfferNumberRef.current,
      kunde: quoteHeader.kunde?.trim() || 'Arbeitsfortschritt',
      datum: quoteHeader.datum || fallbackDateRef.current,
    },
    items: quoteItems,
    positions,
    gewerkOrder,
    editorState: normalizedEditorState,
  };
  const editorSnapshot = JSON.stringify(quoteSaveSnapshot);
  const latestSnapshotRef = useRef<QuoteSaveSnapshot>(quoteSaveSnapshot);
  const latestSnapshotFingerprintRef = useRef(editorSnapshot);
  const lastObservedSnapshotRef = useRef(editorSnapshot);
  latestSnapshotRef.current = quoteSaveSnapshot;
  latestSnapshotFingerprintRef.current = editorSnapshot;
  
  // Local state for discount input to prevent recalculation on every keystroke
  const [localDiscountAmount, setLocalDiscountAmount] = useState<string>(discountAmount.toString());
  const discountDebounceRef = useRef<NodeJS.Timeout | null>(null);
  
  // discountAmount sync removed — causes React #185 nested update on mount.
  // useState initializer already has correct value. clearDiscount re-renders with new value.
  
  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (discountDebounceRef.current) {
        clearTimeout(discountDebounceRef.current);
      }
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      if (autoSaveStatusResetRef.current) {
        clearTimeout(autoSaveStatusResetRef.current);
      }
      quoteAutosaveQueueRef.current?.startDocument(null);
    };
  }, []);

  const scheduleAutoSaveStatusReset = useCallback((delayMs: number) => {
    if (autoSaveStatusResetRef.current) {
      clearTimeout(autoSaveStatusResetRef.current);
    }

    autoSaveStatusResetRef.current = setTimeout(() => {
      setAutoSaveStatus('idle');
    }, delayMs);
  }, []);

  const bindQuoteIdentity = useCallback((identity: QuotePersistenceIdentity) => {
    setCurrentQuotePersistence(identity);
    setHasSaveConflict(false);
    window.history.replaceState(
      {},
      '',
      buildQuoteEditorUrl(identity.slot, window.location.search),
    );
  }, [setCurrentQuotePersistence]);

  const bindSavedQuote = useCallback((savedData: QuoteDataPackage): QuotePersistenceIdentity => {
    const identity = getSavedQuotePersistenceIdentity(savedData);
    bindQuoteIdentity(identity);
    return identity;
  }, [bindQuoteIdentity]);

  if (quoteAutosaveQueueRef.current === null) {
    quoteAutosaveQueueRef.current = createQuoteAutosaveQueue<QuoteSaveSnapshot>({
      async save({ snapshot, identity }) {
        const filename = `${getQuoteFileStem({
          header: snapshot.header,
        }, 'Arbeitsfortschritt')} (Arbeitsfortschritt)`;
        const savedData = await saveQuoteData({
          ...buildQuoteSaveRequest(snapshot, identity, 'save'),
          filename,
        });
        return getSavedQuotePersistenceIdentity(savedData);
      },
      isConflict: isQuoteRevisionConflict,
      onRequestStart() {
        if (autoSaveStatusResetRef.current) {
          clearTimeout(autoSaveStatusResetRef.current);
          autoSaveStatusResetRef.current = null;
        }
        setAutoSaveStatus('saving');
      },
      onSaved({ identity }) {
        bindQuoteIdentity(identity);
        setLastAutoSave(new Date());
        setAutoSaveStatus('saved');
        scheduleAutoSaveStatusReset(2000);
      },
      onConflict() {
        if (autoSaveTimerRef.current) {
          clearTimeout(autoSaveTimerRef.current);
          autoSaveTimerRef.current = null;
        }
        setHasSaveConflict(true);
        setAutoSaveStatus('error');
      },
      onError(error) {
        console.error('Error auto-saving work progress:', error);
        setAutoSaveStatus('error');
        scheduleAutoSaveStatusReset(3000);
      },
    });
    quoteAutosaveQueueRef.current.startDocument(
      currentQuoteSlot === null
        ? null
        : {
            slot: currentQuoteSlot,
            storageRevision: currentQuoteStorageRevision ?? 0,
          },
      quoteSaveSnapshot,
    );
  }
  const quoteAutosaveQueue = quoteAutosaveQueueRef.current;
  
  // Debounced discount update - only update store after user stops typing
  const handleDiscountChange = useCallback((value: string) => {
    const normalizedInput = value.replace(',', '.');
    if (normalizedInput !== '' && !/^\d*\.?\d{0,2}$/.test(normalizedInput)) {
      return;
    }

    setLocalDiscountAmount(normalizedInput);
    
    // Clear any pending debounce
    if (discountDebounceRef.current) {
      clearTimeout(discountDebounceRef.current);
    }
    
    // Debounce the store update to avoid recalculating on every keystroke
    discountDebounceRef.current = setTimeout(() => {
      const parsedValue = parseFloat(normalizedInput) || 0;
      const clampedValue = discountType === 'percent'
        ? Math.min(100, Math.max(0, parsedValue))
        : Math.max(0, parsedValue);
      // Only update store, don't apply to prices yet (that happens when clicking the button)
      setDiscount(clampedValue, discountType, false); // false = don't apply to prices
    }, 300);
  }, [discountType, setDiscount]);

  // KI-Formulierungshilfe je Titel: der Titel (Ebene 1) ist der Gewerk-Name der
  // Zeilen. Übernehmen benennt ihn über ALLE Zeilen der Gruppe um — über den
  // bestehenden, abgesicherten updatePosition-Pfad (Untertitel-Metadatum-Regel,
  // gewerkOrder-Pflege, Auto-Renummerierung inklusive). Es wird ausschließlich
  // der Titel-TEXT geschrieben; Mengen/Preise/Positionsnummern bleiben unberührt.
  const titelAiTarget: PositionAiTextTarget | null = titelAiName === null
    ? null
    : {
        scopeKey: `titel:${titelAiName}`,
        dialogTitle: `KI-Formulierungshilfe — Titel: ${titelAiName}`,
        gewerk: titelAiName,
        felder: [{ key: 'titel', label: 'Titel', text: titelAiName }],
      };

  const handleTitelAiApply = (feldKey: string, text: string) => {
    if (feldKey !== 'titel' || titelAiName === null) {
      return;
    }
    // Titel ist einzeilig: Zeilenumbrüche aus dem KI-Text zu Leerzeichen glätten.
    const newName = text.replace(/\s+/g, ' ').trim();
    const oldName = titelAiName;
    if (!newName || newName === oldName) {
      return;
    }

    const state = useOfferStore.getState();
    const matchesGroup = (gewerk: string | null | undefined) =>
      ((gewerk ?? '').trim() || 'Allgemein') === oldName;
    const ids = state.positions.filter((p) => matchesGroup(p.gewerk)).map((p) => p.id);
    ids.forEach((id) => {
      useOfferStore.getState().updatePosition(id, { gewerk: newName });
    });

    // updatePosition hängt einen neuen Gewerk-Namen ans ENDE von gewerkOrder —
    // für ein Umbenennen soll der Titel aber seinen Platz behalten: neuen Namen
    // an die Stelle des alten setzen, Duplikate (Gruppen-Zusammenlegung) dedupen.
    const orderAfter = useOfferStore.getState().gewerkOrder;
    const replaced = orderAfter
      .map((n) => (n === oldName ? newName : n))
      .filter((n, i, arr) => arr.indexOf(n) === i);
    useOfferStore.getState().setGewerkOrder(replaced);

    toast({
      title: 'Titel umbenannt',
      description: `"${oldName}" heißt jetzt "${newName}" (${ids.length} Position${ids.length !== 1 ? 'en' : ''}).`,
    });
  };

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end for Gewerk reordering
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = gewerkOrder.findIndex(id => id === active.id);
      const newIndex = gewerkOrder.findIndex(id => id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        reorderGewerk(oldIndex, newIndex);
      }
    }
  };

  useEffect(() => {
    const nextIdentity: QuotePersistenceIdentity | null = currentQuoteSlot === null
      ? null
      : {
          slot: currentQuoteSlot,
          storageRevision: currentQuoteStorageRevision ?? 0,
        };
    const queuedIdentity = quoteAutosaveQueue.getState().identity;
    const isSameIdentity = (
      queuedIdentity?.slot === nextIdentity?.slot
      && queuedIdentity?.storageRevision === nextIdentity?.storageRevision
    );

    if (isSameIdentity) {
      return;
    }

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    quoteAutosaveQueue.startDocument(nextIdentity, latestSnapshotRef.current);
    lastObservedSnapshotRef.current = latestSnapshotFingerprintRef.current;
    setHasSaveConflict(false);
    setAutoSaveStatus('idle');
  }, [
    currentQuoteSlot,
    currentQuoteStorageRevision,
    quoteAutosaveQueue,
  ]);

  useEffect(() => {
    if (
      isGAEBDatabase
      || hasSaveConflict
      || isDocumentTransitionRef.current
    ) {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      return;
    }

    if (editorSnapshot === lastObservedSnapshotRef.current) {
      return;
    }
    lastObservedSnapshotRef.current = editorSnapshot;

    if (quoteItems.length === 0) {
      return;
    }

    quoteAutosaveQueue.reportSnapshot(latestSnapshotRef.current);
    if (quoteAutosaveQueue.getState().isFlushing) {
      return;
    }

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveTimerRef.current = null;
      void quoteAutosaveQueue.flush().catch(() => {
        // Queue callbacks own status/conflict reporting; avoid an unhandled rejection.
      });
    }, 3000);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
  }, [
    editorSnapshot,
    hasSaveConflict,
    isGAEBDatabase,
    quoteAutosaveQueue,
    quoteItems.length,
  ]);

  // Handle save work progress
  const handleSaveProgress = async () => {
    // For GAEB databases, save directly to GAEB endpoint
    if (isGAEBDatabase && currentDatabaseId) {
      setIsSaving(true);
      try {
        // Get all modified positions (items with ourPrice, ourComment, or adjustedQuantity)
        const entriesToSave = positions
          .filter(p => (p as any).type === 'item')
          .map(p => {
            const gaebEntry = p as any;
            // CRITICAL: Use null instead of undefined for reset values
            // JSON.stringify drops undefined values, but preserves null
            // This allows the server to distinguish between "not sent" and "reset to original"
            const adjustedQty = gaebEntry.adjustedQuantity;
            const price = gaebEntry.ourPrice;
            const comment = gaebEntry.ourComment;
            return {
              // CRITICAL: artikelId must match the database entry's artikelId for updates to work
              artikelId: gaebEntry.artikelId || p.originalId,
              ourPrice: price === undefined ? null : price,
              // null signals "clear comment" - same pattern as price/quantity
              ourComment: (comment === undefined || comment === '') ? null : comment,
              adjustedQuantity: adjustedQty === undefined ? null : adjustedQty
            };
          });

        if (entriesToSave.length === 0) {
          toast({
            title: "Keine Daten zum Speichern",
            description: "Es wurden keine LV-Positionen gefunden.",
            variant: "destructive"
          });
          return;
        }

        const response = await fetch('/api/gaeb/save', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            databaseId: currentDatabaseId,
            entries: entriesToSave
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Save failed' }));
          throw new Error(errorData.error || `HTTP ${response.status}`);
        }

        toast({
          title: "X83-Daten gespeichert",
          description: `${entriesToSave.length} Positionen wurden gespeichert.`,
          variant: "default"
        });

        console.log('GAEB data saved successfully:', entriesToSave.length, 'entries');
      } catch (error) {
        console.error('Error saving GAEB data:', error);
        toast({
          title: "Fehler beim Speichern",
          description: error instanceof Error ? error.message : 'Unbekannter Fehler beim Speichern',
          variant: "destructive"
        });
      } finally {
        setIsSaving(false);
      }
      return;
    }

    const snapshot = latestSnapshotRef.current;
    if (snapshot.items.length === 0) {
      toast({
        title: "Keine Daten zum Speichern",
        description: "Fügen Sie erst Positionen mit Mengen hinzu, bevor Sie speichern.",
        variant: "destructive"
      });
      return;
    }

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    setIsSaving(true);
    quoteAutosaveQueue.reportSnapshot(snapshot);

    try {
      await quoteAutosaveQueue.flush();
      toast({
        title: "Arbeitsfortschritt gespeichert",
        description: `"${snapshot.header.angebot_nr}" wurde erfolgreich gespeichert. Sie können später weiterarbeiten oder exportieren.`,
        variant: "default"
      });
    } catch (error) {
      console.error('Error saving work progress:', error);
      toast({
        title: isQuoteRevisionConflict(error) ? 'Speicherkonflikt' : 'Fehler beim Speichern',
        description: isQuoteRevisionConflict(error)
          ? 'Der sichtbare Entwurf wurde zwischenzeitlich geändert. Laden Sie ihn neu oder speichern Sie Ihre Fassung als neuen Entwurf.'
          : error instanceof Error
            ? error.message
            : 'Unbekannter Fehler beim Speichern des Arbeitsfortschritts',
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAs = async () => {
    if (isGAEBDatabase) {
      return;
    }

    const initialSnapshot = latestSnapshotRef.current;
    if (initialSnapshot.items.length === 0) {
      toast({
        title: "Keine Daten zum Speichern",
        description: "Fügen Sie erst Positionen mit Mengen hinzu, bevor Sie speichern.",
        variant: "destructive"
      });
      return;
    }

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    const previousIdentity = currentQuoteSlot === null
      ? null
      : {
          slot: currentQuoteSlot,
          storageRevision: currentQuoteStorageRevision ?? 0,
        };
    const hadTerminalConflict = quoteAutosaveQueue.getState().hasTerminalConflict;
    isDocumentTransitionRef.current = true;
    setIsSaving(true);
    const saveAsGeneration = quoteAutosaveQueue.startDocument(null, initialSnapshot);

    try {
      // Wait for a superseded request to settle before the save-as request starts.
      await quoteAutosaveQueue.flush();
      const snapshotToSave = latestSnapshotRef.current;
      const filename = `${getQuoteFileStem({
        header: snapshotToSave.header,
      }, 'Arbeitsfortschritt')} (Arbeitsfortschritt)`;
      const savedData = await saveQuoteData({
        ...buildQuoteSaveRequest(snapshotToSave, null, 'save-as'),
        filename,
      });
      if (quoteAutosaveQueue.getState().generation !== saveAsGeneration) {
        isDocumentTransitionRef.current = false;
        return;
      }
      const savedIdentity = bindSavedQuote(savedData);

      quoteAutosaveQueue.startDocument(savedIdentity, snapshotToSave);
      lastObservedSnapshotRef.current = JSON.stringify(snapshotToSave);
      isDocumentTransitionRef.current = false;

      if (latestSnapshotFingerprintRef.current !== JSON.stringify(snapshotToSave)) {
        lastObservedSnapshotRef.current = latestSnapshotFingerprintRef.current;
        quoteAutosaveQueue.reportSnapshot(latestSnapshotRef.current);
        void quoteAutosaveQueue.flush().catch(() => {
          // Queue callbacks own status/conflict reporting.
        });
      }

      toast({
        title: "Arbeitsfortschritt gespeichert",
        description: `"${snapshotToSave.header.angebot_nr}" wurde als neuer Entwurf gespeichert.`,
        variant: "default"
      });
    } catch (error) {
      console.error('Error saving work progress as a new quote:', error);
      if (quoteAutosaveQueue.getState().generation !== saveAsGeneration) {
        isDocumentTransitionRef.current = false;
        return;
      }
      quoteAutosaveQueue.startDocument(previousIdentity, initialSnapshot);
      isDocumentTransitionRef.current = false;
      if (!hadTerminalConflict) {
        lastObservedSnapshotRef.current = latestSnapshotFingerprintRef.current;
        quoteAutosaveQueue.reportSnapshot(latestSnapshotRef.current);
      }
      setHasSaveConflict(hadTerminalConflict);
      toast({
        title: "Fehler beim Speichern",
        description: error instanceof Error
          ? error.message
          : 'Unbekannter Fehler beim Speichern des Arbeitsfortschritts',
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReloadAfterConflict = async () => {
    if (currentQuoteSlot === null) {
      return;
    }

    setIsSaving(true);
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    try {
      await loadQuoteData(currentQuoteSlot);
      setHasSaveConflict(false);
      setAutoSaveStatus('idle');
      window.history.replaceState(
        {},
        '',
        buildQuoteEditorUrl(currentQuoteSlot, window.location.search),
      );
      toast({
        title: 'Entwurf neu geladen',
        description: `Entwurf #${currentQuoteSlot} entspricht wieder dem gespeicherten Stand.`,
      });
    } catch (error) {
      toast({
        title: 'Neu laden fehlgeschlagen',
        description: error instanceof Error ? error.message : 'Unbekannter Fehler',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Export quote (creates invoice draft)
  const handleCreateInvoice = async () => {
    // Bedarfs-/Alternativpositionen sind NICHT beauftragt → nicht in den Rechnungs-
    // entwurf übernehmen (sonst würden Positionen fakturiert, die die Angebotssumme
    // ausdrücklich ausschließt). Sichtbar melden, nicht still weglassen.
    const excludedCount = quoteItems.filter(
      (q) => Number(q.menge) > 0 && isExcludedFromMainSum(q),
    ).length;

    const normalizedItems = quoteItems
      .filter((q) => !isExcludedFromMainSum(q))
      .map((q) => {
        const quantity = Number(q.menge) || 0;
        const unitPrice = Number(q.preis) || 0;
        const vatRate = Number(q.umsatzsteuer) || 19;
        return {
          ...q,
          quantity,
          unitPrice,
          vatRate,
        };
      })
      .filter((q) => q.quantity > 0);

    if (excludedCount > 0) {
      toast({
        title: `${excludedCount} Bedarfs-/Alternativposition(en) nicht übernommen`,
        description: 'Nicht beauftragte Positionen werden nicht fakturiert. Bei Beauftragung die Position zuerst auf "Normal" stellen.',
      });
    }

    if (normalizedItems.length === 0) {
      toast({
        title: 'Keine Positionen mit Menge vorhanden',
        description: 'Bitte geben Sie zuerst Mengen ein.',
        variant: 'destructive',
      });
      return;
    }

    setIsCreatingInvoice(true);
    try {
      let effectiveSlot = quoteAutosaveQueue.getState().identity?.slot
        ?? currentQuoteSlot
        ?? undefined;
      if (!effectiveSlot) {
        quoteAutosaveQueue.reportSnapshot(latestSnapshotRef.current);
        await quoteAutosaveQueue.flush();
        effectiveSlot = quoteAutosaveQueue.getState().identity?.slot;
        if (!effectiveSlot) {
          throw new Error('Der Angebotsentwurf konnte keinem Speicherplatz zugeordnet werden.');
        }
      }

      const searchParams = new URLSearchParams(window.location.search);
      const softecProjectId = searchParams.get('projectId')?.trim() || undefined;

      const res = await fetch('/api/quotes/to-invoice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          positions: normalizedItems.map((q, index) => ({
            title: (q.name || q.beschreibung || `Position ${index + 1}`).toString().trim(),
            description: q.beschreibung,
            quantity: q.quantity,
            unit: (q.einheit || 'Stk').toString().trim() || 'Stk',
            unitPrice: q.unitPrice,
            vatRate: q.vatRate,
            positionNumber: q.positionNumber,
            gewerk: q.gewerk,
            budgetTaskId: q.budgetTaskId,
          })),
          clientName: quoteHeader.kunde,
          clientAddress: quoteHeader.anschrift,
          projectName: quoteHeader.bauvorhaben,
          // Firmen-Briefkopf-Profil des Angebots 1:1 in die Rechnung übernehmen
          // (Codex Runde 8, Finding 1) — sonst friert die Finalisierung das
          // aktuell aktive Profil statt des Angebots-Profils ein.
          companyProfileId: quoteHeader.companyProfileId,
          softecProjectId,
          slot: effectiveSlot,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Konvertierung fehlgeschlagen');
      }

      if (data?.data?.invoice?.id) {
        toast({
          title: 'Rechnungsentwurf erstellt',
          description: `Entwurf mit ${normalizedItems.length} Positionen erstellt.`,
        });
        navigate(`/invoices?edit=${data.data.invoice.id}`);
      } else {
        throw new Error('Kein Export möglich');
      }
    } catch (error) {
      console.error('Error exporting quote:', error);
      toast({
        title: 'Fehler',
        description: error instanceof Error ? error.message : 'Rechnung konnte nicht erstellt werden',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingInvoice(false);
    }
  };

  return (
    <div className="w-80 bg-white border-l border-gray-200 flex flex-col h-full overflow-hidden">
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="p-4 space-y-6">
          


          {/* Gewerk Order Section with Drag & Drop */}
          {gewerkGroups.length > 0 && (
            <div className="space-y-4">
              <Collapsible open={isGewerkOpen} onOpenChange={setIsGewerkOpen}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="w-full justify-between p-0 h-auto">
                    <h3 className="text-lg font-semibold text-gray-900">Gewerke</h3>
                    {isGewerkOpen ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-2">
                  <p className="text-sm text-gray-600 mb-3">
                    Ziehen Sie die Gewerke, um die Reihenfolge für den Export zu ändern:
                  </p>
                  
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={gewerkOrder}
                      strategy={verticalListSortingStrategy}
                    >
                      {gewerkGroups.map((group) => (
                        <SortableGewerkItem
                          key={group.name}
                          id={group.name}
                          name={group.name}
                          items={group.items}
                          netTotal={group.netTotal}
                          onAiClick={isGAEBDatabase ? undefined : () => setTitelAiName(group.name)}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </CollapsibleContent>
              </Collapsible>
            </div>
          )}

          {/* Discount Section */}
          {gewerkGroups.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Rabatt</h3>
              <div className={`p-4 rounded-lg space-y-3 ${isDiscountAppliedToPrices ? 'bg-orange-50 border-2 border-orange-300' : 'bg-purple-50'}`}>
                
                {/* Active Discount Warning Banner */}
                {isDiscountAppliedToPrices && (
                  <div className="bg-orange-100 border border-orange-300 rounded-lg p-2 flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-orange-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-xs font-medium text-orange-800">
                        Preise wurden um {discountAmount}% reduziert
                      </p>
                      <p className="text-xs text-orange-600">
                        Alle Einzelpreise sind rabattiert
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearDiscount}
                      className="h-7 px-2 text-xs bg-white border-orange-400 text-orange-700 hover:bg-orange-50"
                    >
                      <Undo2 className="h-3 w-3 mr-1" />
                      Rückgängig
                    </Button>
                  </div>
                )}

                {/* Discount Type Toggle — Mode-Umschalter, keine primaere Aktion:
                    aktiv = secondary (mehr Gewicht), inaktiv = outline. "Speichern"
                    bleibt der einzige primaere (solid-blaue) Button im Editor
                    (Welle 1.1/3.1 UX-Entlastung Farb-Rollen). */}
                <div className="flex gap-2">
                  <Button
                    variant={discountType === 'percent' ? 'secondary' : 'outline'}
                    size="sm"
                    onClick={() => setDiscount(parseFloat(localDiscountAmount) || 0, 'percent', false)}
                    className="flex-1"
                    disabled={isDiscountAppliedToPrices}
                  >
                    %
                  </Button>
                  <Button
                    variant={discountType === 'absolute' ? 'secondary' : 'outline'}
                    size="sm"
                    onClick={() => setDiscount(parseFloat(localDiscountAmount) || 0, 'absolute', false)}
                    className="flex-1"
                    disabled={isDiscountAppliedToPrices}
                    title="Fester Euro-Betrag als Rabatt"
                  >
                    €
                  </Button>
                </div>

                {/* Discount Input */}
                <div className="space-y-1">
                  <Label htmlFor="discount-amount" className="text-xs text-gray-600">
                    Rabatt ({discountType === 'percent' ? '%' : '€'})
                  </Label>
                  <Input
                    id="discount-amount"
                    type="text"
                    inputMode="decimal"
                    value={localDiscountAmount}
                    onChange={(e) => handleDiscountChange(e.target.value)}
                    placeholder="0"
                    className="text-sm"
                    disabled={isDiscountAppliedToPrices}
                  />
                </div>

                {/* Apply to Einzelpreise Button */}
                <div className="space-y-2">
                  <Label className="text-xs text-gray-600">Einzelpreise rabattieren:</Label>
                  {/* Aktionszustand: sekundär (Rollen-Sprache, Welle 1.1/3.1) —
                      kein gefüllter Primär (der bliebe sonst neben "Speichern"
                      ein zweiter from-primary) und kein Aktions-Orange. Der
                      "✓ angewandt"-Zustand ist reiner Status: dezent (ghost) mit
                      grünem Häkchen (Grün = Status, erlaubt), zudem disabled. */}
                  <Button
                    variant={isDiscountAppliedToPrices ? 'ghost' : 'secondary'}
                    size="sm"
                    onClick={() => applyDistributedDiscount()}
                    className={`w-full text-xs ${isDiscountAppliedToPrices ? 'text-green-700' : ''}`}
                    disabled={isDiscountAppliedToPrices || discountType === 'absolute' || parseFloat(localDiscountAmount) <= 0}
                    title={discountType === 'absolute' ? 'Nur mit Prozent-Rabatt möglich' : parseFloat(localDiscountAmount) <= 0 ? 'Bitte erst Rabatt eingeben' : 'Rabatt wird auf alle Einzelpreise angewendet'}
                  >
                    {isDiscountAppliedToPrices 
                      ? `✓ ${discountAmount}% angewandt` 
                      : `Alle Einzelpreise um ${localDiscountAmount || '0'}% reduzieren`}
                  </Button>
                  {parseFloat(localDiscountAmount) > 0 && !isDiscountAppliedToPrices && discountType === 'percent' && (
                    <p className="text-xs text-orange-600 mt-1">
                      ⚠️ Dies ändert alle {quoteItems.length} Einzelpreise dauerhaft
                    </p>
                  )}
                </div>

                {/* Discount Preview */}
                {parseFloat(localDiscountAmount) > 0 && !isDiscountAppliedToPrices && (
                  <div className="bg-white p-2 rounded border border-purple-200 space-y-1">
                    <div className="text-xs text-gray-600">
                      {discountType === 'percent' 
                        ? `${localDiscountAmount}% von Gesamtsumme (${quoteItems.length} Positionen)`
                        : `${formatCurrency(parseFloat(localDiscountAmount) || 0)} von Gesamtsumme`
                      }
                    </div>
                    <div className="text-sm font-medium text-purple-600">
                      Ersparnis: {formatCurrency(
                        discountType === 'percent'
                          ? (totalNet * (parseFloat(localDiscountAmount) || 0)) / 100
                          : parseFloat(localDiscountAmount) || 0
                      )}
                    </div>
                  </div>
                )}

                {/* Show savings summary when prices are modified */}
                {isDiscountAppliedToPrices && Object.keys(originalPrices).length > 0 && (
                  <div className="bg-white p-2 rounded border border-orange-200 space-y-1">
                    <div className="text-xs text-gray-600">Gesamtersparnis:</div>
                    <div className="text-sm font-medium text-green-600">
                      {formatCurrency(
                        Object.entries(originalPrices).reduce((sum, [id, origPrice]) => {
                          const item = quoteItems.find(q => q.id === parseInt(id));
                          if (item) {
                            const currentPrice = typeof item.preis === 'string' ? parseFloat(item.preis) : item.preis;
                            return sum + ((origPrice - currentPrice) * (item.menge || 0));
                          }
                          return sum;
                        }, 0)
                      )}
                    </div>
                  </div>
                )}

                {/* Clear Button - show when there's a discount value */}
                {(parseFloat(localDiscountAmount) > 0 || isDiscountAppliedToPrices) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      clearDiscount();
                      setLocalDiscountAmount('0');
                    }}
                    className="w-full text-xs"
                  >
                    {isDiscountAppliedToPrices ? 'Rabatt zurücksetzen' : 'Rabatt löschen'}
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Grand Total */}
          {gewerkGroups.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Gesamtsumme</h3>
              <div className="bg-blue-50 p-4 rounded-lg space-y-2">
                <div className="flex items-center justify-between gap-3 pb-1">
                  <Label className="text-xs text-gray-600">MwSt-Satz</Label>
                  <Select
                    value={String(vatRate)}
                    onValueChange={(value) => setGlobalVatRate(Number(value))}
                  >
                    <SelectTrigger className="h-8 w-24 text-xs border-blue-200 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="19">19%</SelectItem>
                      <SelectItem value="7">7%</SelectItem>
                      <SelectItem value="0">0%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Netto:</span>
                  <span>{formatCurrency(totalNet)}</span>
                </div>

                {/* Positionen ohne erfassten Preis (preisFehlt): Summe ist unvollständig —
                    dieselbe Warnung, die auch das PDF unter dem Summenblock druckt. */}
                {countMissingPrices(quoteItems) > 0 && (
                  <div className="flex justify-between text-xs text-red-600 font-medium">
                    <span>{countMissingPrices(quoteItems)} Position(en) ohne Preis:</span>
                    <span>nicht in Summe („–")</span>
                  </div>
                )}

                {/* Bedarfs-/Alternativpositionen (nachrichtlich, NICHT in der Summe) */}
                {bedarfNet > 0 && (
                  <div className="flex justify-between text-xs text-amber-600">
                    <span>zzgl. Bedarfspositionen (netto):</span>
                    <span>{formatCurrency(bedarfNet)}</span>
                  </div>
                )}
                {alternativNet > 0 && (
                  <div className="flex justify-between text-xs text-amber-600">
                    <span>zzgl. Alternativpositionen (netto):</span>
                    <span>{formatCurrency(alternativNet)}</span>
                  </div>
                )}

                {/* Discount Line - only show when NOT applied to individual prices */}
                {hasTotalDiscount && (
                  <div className="flex justify-between text-sm text-purple-600 font-medium">
                    <span>Rabatt ({discountType === 'percent' ? `${discountAmount}%` : `${formatCurrency(discountAmount)}`}):</span>
                    <span>-{formatCurrency(totalDiscountValue)}</span>
                  </div>
                )}

                {hasTotalDiscount && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Netto nach Rabatt:</span>
                    <span>{formatCurrency(netAfterTotalDiscount)}</span>
                  </div>
                )}
                
                {/* Applied Discount Info */}
                {isDiscountAppliedToPrices && (
                  <div className="flex justify-between text-sm text-orange-600 font-medium">
                    <span>Rabatt ({discountAmount}% angewandt):</span>
                    <span className="text-green-600">✓ in Preisen</span>
                  </div>
                )}

                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">MwSt. ({vatRate}%):</span>
                  <span>{formatCurrency(totalTaxAfterDiscount)}</span>
                </div>
                <div className="flex justify-between text-lg font-semibold border-t border-blue-200 pt-2">
                  <span>Brutto:</span>
                  <span className="text-blue-900">
                    {formatCurrency(totalGrossAfterDiscount)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Statistics */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Statistiken</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 p-3 rounded-lg text-center">
                <div className="text-2xl font-bold text-blue-600">{quoteItems.length}</div>
                <div className="text-xs text-blue-600">Ausgewählt</div>
              </div>
              <div className="bg-green-50 p-3 rounded-lg text-center">
                <div className="text-2xl font-bold text-green-600">{gewerkGroups.length}</div>
                <div className="text-xs text-green-600">{gewerkGroups.length === 1 ? 'Gewerk' : 'Gewerke'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Export Section at Bottom.
          Etwas knapper (p-2.5/space-y-1.5 statt p-3/space-y-2): die Fußzeile ist
          seit den Export-/Softec-/Rechnung-Buendelungen (Wellen 1.2/1.3) hoch
          genug, dass sie bei kurzen Angeboten (wenige Gewerke) die scrollbare
          Sidebar-Mitte auf ~260px zusammendrückt — genug, um die "Rabatt"-
          Überschrift beim ersten Rendern mitten im Buchstaben abzuschneiden
          (kein Overlay-/z-index-Bug, gemessen via getBoundingClientRect: die
          Überschrift ragt exakt 17px unter die scrollbare Fläche). Die paar
          Pixel weniger Fußzeile reichen, damit die Überschrift wieder
          vollständig sichtbar ist. */}
      <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50">
        <div className="p-2.5 space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            {currentQuoteSlot !== null ? (
              <Badge
                data-testid="badge-current-quote-slot"
                variant="secondary"
                className="text-xs"
              >
                Entwurf #{currentQuoteSlot}
              </Badge>
            ) : (
              <span className="text-xs text-gray-500">Neuer Entwurf</span>
            )}

            {quoteItems.length > 0 && (
              <div className="flex items-center gap-1.5 text-xs min-w-0">
                {autoSaveStatus === 'saving' && (
                  <>
                    <div className="w-2 h-2 border-2 border-blue-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                    <span className="text-blue-600 truncate">Speichert...</span>
                  </>
                )}
                {autoSaveStatus === 'saved' && (
                  <>
                    <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0" />
                    <span className="text-green-600 truncate">Gespeichert {lastAutoSave && lastAutoSave.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</span>
                  </>
                )}
                {autoSaveStatus === 'error' && (
                  <span className="text-red-600 truncate">Speichern fehlgeschlagen</span>
                )}
                {autoSaveStatus === 'idle' && lastAutoSave && (
                  <span className="text-gray-400 truncate">{lastAutoSave.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</span>
                )}
              </div>
            )}
          </div>

          {hasSaveConflict && (
            <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-2 space-y-2">
              <p className="text-xs text-red-700">
                Dieser Entwurf wurde zwischenzeitlich geändert.
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 flex-1 text-xs"
                  onClick={handleReloadAfterConflict}
                  disabled={isSaving}
                >
                  Neu laden
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 flex-1 text-xs"
                  onClick={handleSaveAs}
                  disabled={isSaving}
                >
                  Als neu speichern
                </Button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <Button
              data-testid="button-save-quote"
              type="button"
              onClick={handleSaveProgress}
              disabled={isSaving || quoteItems.length === 0}
              size="sm"
              className="w-full gap-1.5"
            >
              {isSaving ? (
                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}
              Speichern
            </Button>
            <Button
              data-testid="button-save-quote-as"
              type="button"
              onClick={handleSaveAs}
              disabled={isSaving || isGAEBDatabase || quoteItems.length === 0}
              variant="outline"
              size="sm"
              className="w-full text-xs"
            >
              Als neu speichern
            </Button>
          </div>

          {/* PDF Vorschau (haeufigster Export, bleibt sichtbar) */}
          <DocxExport />

          {/* Secondary exports: An Softec senden bleibt eigenstaendig sichtbar
              (kein reiner Datei-Export, sondern eine Cross-App-Aktion); die
              reinen Datei-Exporte sind seit Welle 1.3 der UX-Entlastung im
              "Export ▾"-Menü gebündelt (gleiches Muster wie das "Neu
              erstellen"-Menü auf dem Dashboard, siehe pages/home.tsx). */}
          <div className="space-y-1.5">
            <SoftecExportButton
              slot={currentQuoteSlot || 0}
              disabled={quoteItems.length === 0}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                {/* Keine quoteItems.length===0-Sperre auf dem Trigger selbst:
                    keines der gebündelten Items war vorher so gated (X84Price
                    Import hatte z.B. gar keine disabled-Bedingung) — jedes Item
                    regelt sein eigenes disabled/leer-Verhalten selbst. Ein
                    gesperrter Trigger würde das ganze Menü blockieren, auch
                    wenn einzelne Einträge (z.B. GAEB-Werkzeuge) unabhängig
                    von quoteItems nutzbar sein sollen. */}
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full gap-1.5"
                  data-testid="button-export-menu-trigger"
                >
                  <Download className="h-4 w-4" />
                  Export
                  <ChevronDown className="ml-auto h-3 w-3 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              {/* Breite = exakt die Trigger-Breite (Radix-CSS-Var), nicht ein
                  fixes w-64 (256px): Der Trigger ist w-full in der 320px
                  breiten Sidebar (abzgl. p-3-Padding ≈ 296px). Mit einer
                  schmaleren festen Breite ragten "Als neu speichern"/"PDF
                  Vorschau"/Entwurf-Badge rechts unter dem (nach oben
                  aufklappenden) Menü hervor — sichtbarer Durcheinander-Bug,
                  kein Animations-Timing-Artefakt (mit waitForAnimation
                  reproduzierbar identisch). */}
              <DropdownMenuContent
                align="start"
                className="w-[var(--radix-dropdown-menu-trigger-width)]"
              >
                {/* Reihenfolge = bisherige Reihenfolge der Einzel-Buttons.
                    "Einfache" Exporte (Blanko-LV, Großhandel-Anfrage,
                    Urkalkulation) laufen als reine Aktions-Menüpunkte; ihr
                    In-Flight-Zustand liegt im persistenten Parent (exportBusy),
                    damit ein laufender Export nach Menü-Schließen/-Öffnen NICHT
                    ein zweites Mal auslösbar ist (Regression-Fix). */}
                <BlankoLvExport asMenuItem busy={exportBusy} onRunningChange={setExportBusy} />
                <GrosshandelAnfrageExport asMenuItem busy={exportBusy} onRunningChange={setExportBusy} />
                {/* Dialog-Exporte: der Menü-Eintrag öffnet nur den controlled
                    Geschwister-Dialog (siehe unten) und lässt das Dropdown normal
                    schließen — kein preventDefault, damit das geschlossene Menü
                    nach dem Dialog nichts über Speichern/PDF überlappt.
                    GAEB-Export/Teil-LV bleiben auf den GAEB-Modus gegated
                    (Progressive Disclosure, Welle 1.4); X84 bleibt ungated. */}
                {isGaebMode && (
                  <DropdownMenuItem
                    onSelect={() => setGaebExportOpen(true)}
                    data-testid="button-gaeb-export"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    GAEB Export
                  </DropdownMenuItem>
                )}
                <UrkalkulationExport asMenuItem busy={exportBusy} onRunningChange={setExportBusy} />
                <DropdownMenuItem
                  onSelect={() => setX84ImportOpen(true)}
                  data-testid="button-x84-price-import"
                >
                  <FileUp className="mr-2 h-4 w-4" />
                  X84 Preise importieren
                </DropdownMenuItem>
                {isGaebMode && (
                  <DropdownMenuItem
                    onSelect={() => setTeilExportOpen(true)}
                    data-testid="button-gaeb-teil-export"
                  >
                    <Scissors className="mr-2 h-4 w-4" />
                    Teil-LV exportieren
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Controlled Dialog-Exporte als GESCHWISTER des DropdownMenu.
                Ihr DialogContent portalt ohnehin an document.body; entscheidend
                ist, dass sie NICHT Kind des Menü-Contents sind — so überlebt der
                Dialog das Menü-Schließen und das (geschlossene) Dropdown
                überlappt nach Abbrechen/Übernehmen nichts. */}
            <GAEBExport open={gaebExportOpen} onOpenChange={setGaebExportOpen} />
            <X84PriceImport open={x84ImportOpen} onOpenChange={setX84ImportOpen} />
            <GaebTeilExport open={teilExportOpen} onOpenChange={setTeilExportOpen} />
          </div>

          {/* Rechnung erstellen: Geld-Aktion, deshalb eigene, deutlich
              abgesetzte Zone statt Teil des Export-Sammel-Clusters (Welle 1.2).
              Position bleibt unveraendert (direkt nach dem Export-Cluster) —
              nur optisch abgesetzt, damit sie bei hasInvoiceAccess nicht in
              der Klickreihenfolge springt.
              variant="secondary" ergab ein unentschiedenes Hellblau/Lavendel,
              das kaum von "Als neu speichern" zu unterscheiden war (Designer-
              Review nach Welle 1.1/3.1). Jetzt: Outline mit dezentem, dunklem
              Akzent (kein Füll-Lavendel, kein zweiter from-primary neben
              "Speichern", keine Status-Farbe gruen/orange als Aktionsfarbe,
              siehe button.tsx-Farbrollen) — bewusst dunkler/kontrastreicher
              als das helle Blau-Outline von "PDF Vorschau", damit die beiden
              nicht ineinander verschwimmen. */}
          {hasInvoiceAccess && (
            <div className="pt-1.5 mt-0.5 border-t border-gray-200">
              <Button
                onClick={handleCreateInvoice}
                disabled={isCreatingInvoice || quoteItems.filter(q => q.menge > 0).length === 0}
                variant="outline"
                size="sm"
                className="w-full flex items-center justify-center gap-2 border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white"
              >
                {isCreatingInvoice ? (
                  <>
                    <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    Erstelle...
                  </>
                ) : (
                  <>
                    <FileDown className="h-4 w-4" />
                    Rechnung erstellen
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* KI-Formulierungshilfe je Titel — eine Dialog-Instanz, Übernahme nur
          nach Vorher/Nachher-Entscheidung des Nutzers. */}
      <PositionAiTextDialog
        target={titelAiTarget}
        onClose={() => setTitelAiName(null)}
        onApply={handleTitelAiApply}
      />
    </div>
  );
}
